import { buildCsv } from "./csv";
import type { AuditInfo } from "./schemas";
import type { AssetView } from "../assets/types";
import type { AssetAssignmentView } from "../asset-assignments/types";
import type { SageOfficeView } from "../sage-offices/types";
import type { WorkspaceView } from "../workspaces/types";
import type { WorkspaceAssignmentView } from "../workspace-assignments/types";

/** Mirror of `ExportLimits.MaxRows` on the API. */
export const EXPORT_MAX_ROWS = 10_000;

export interface ExportJob {
    readonly csv: string;
    readonly truncated: boolean;
    readonly rowCount: number;
}

function cell(value: string | number | boolean | null | undefined): string {
    if (value === null || value === undefined) return "";
    if (typeof value === "boolean") return value ? "true" : "false";
    return String(value);
}

function stamp(audit: AuditInfo | null | undefined): string {
    return audit?.timeStamp ?? "";
}

function actor(audit: AuditInfo | null | undefined): string {
    return audit?.actor ?? "";
}

export function buildAssetsExportCsv(
    assets: readonly AssetView[],
    openAssignments: readonly AssetAssignmentView[],
): string {
    const assignmentByAssetId = new Map<string, AssetAssignmentView>();
    for (const a of openAssignments) {
        if (!assignmentByAssetId.has(a.assetId)) assignmentByAssetId.set(a.assetId, a);
    }

    return buildCsv(
        [
            "id",
            "sageOfficeId",
            "type",
            "make",
            "model",
            "serialNumber",
            "assetTag",
            "purchaseDate",
            "purchasePrice",
            "assignedToWorkspaceId",
            "assignedToUserId",
            "currentAssignmentId",
            "isDeleted",
            "createdAt",
            "createdBy",
            "modifiedAt",
            "modifiedBy",
            "deletedAt",
            "deletedBy",
        ],
        assets.map((row) => {
            const assignment = assignmentByAssetId.get(row.id);
            return [
                cell(row.id),
                cell(row.sageOfficeId),
                cell(row.type),
                cell(row.make),
                cell(row.model),
                cell(row.serialNumber),
                cell(row.assetTag),
                cell(row.purchaseDate),
                cell(row.purchasePrice),
                cell(assignment?.assignedToWorkspaceId),
                cell(assignment?.assignedToUserId),
                cell(row.currentAssignmentId),
                cell(row.isDeleted),
                stamp(row.created),
                actor(row.created),
                stamp(row.modified),
                actor(row.modified),
                stamp(row.deleted),
                actor(row.deleted),
            ];
        }),
    );
}

export function buildWorkspacesExportCsv(rows: readonly WorkspaceView[]): string {
    return buildCsv(
        [
            "id",
            "sageOfficeId",
            "type",
            "name",
            "friendlyName",
            "floorplanElementId",
            "currentAssignmentId",
            "isDeleted",
            "createdAt",
            "createdBy",
            "modifiedAt",
            "modifiedBy",
            "deletedAt",
            "deletedBy",
        ],
        rows.map((row) => [
            cell(row.id),
            cell(row.sageOfficeId),
            cell(row.type),
            cell(row.name),
            cell(row.friendlyName),
            cell(row.floorplanElementId),
            cell(row.currentAssignmentId),
            cell(row.isDeleted),
            stamp(row.created),
            actor(row.created),
            stamp(row.modified),
            actor(row.modified),
            stamp(row.deleted),
            actor(row.deleted),
        ]),
    );
}

export function buildSageOfficesExportCsv(rows: readonly SageOfficeView[]): string {
    return buildCsv(
        [
            "id",
            "name",
            "street1",
            "street2",
            "city",
            "region",
            "postalCode",
            "country",
            "isDeleted",
            "createdAt",
            "createdBy",
            "modifiedAt",
            "modifiedBy",
            "deletedAt",
            "deletedBy",
        ],
        rows.map((row) => [
            cell(row.id),
            cell(row.name),
            cell(row.address.street1),
            cell(row.address.street2),
            cell(row.address.city),
            cell(row.address.region),
            cell(row.address.postalCode),
            cell(row.address.country),
            cell(row.isDeleted),
            stamp(row.created),
            actor(row.created),
            stamp(row.modified),
            actor(row.modified),
            stamp(row.deleted),
            actor(row.deleted),
        ]),
    );
}

export function buildAssetAssignmentsExportCsv(rows: readonly AssetAssignmentView[]): string {
    return buildCsv(
        [
            "id",
            "assetId",
            "targetKind",
            "assignedToUserId",
            "assignedToWorkspaceId",
            "isOpen",
            "assignedAt",
            "assignedBy",
            "releasedAt",
            "releasedBy",
        ],
        rows.map((row) => [
            cell(row.id),
            cell(row.assetId),
            cell(row.targetKind),
            cell(row.assignedToUserId),
            cell(row.assignedToWorkspaceId),
            cell(row.isOpen),
            stamp(row.assigned),
            actor(row.assigned),
            stamp(row.released),
            actor(row.released),
        ]),
    );
}

export function buildWorkspaceAssignmentsExportCsv(
    rows: readonly WorkspaceAssignmentView[],
): string {
    return buildCsv(
        [
            "id",
            "workspaceId",
            "userId",
            "isOpen",
            "assignedAt",
            "assignedBy",
            "releasedAt",
            "releasedBy",
        ],
        rows.map((row) => [
            cell(row.id),
            cell(row.workspaceId),
            cell(row.userId),
            cell(row.isOpen),
            stamp(row.assigned),
            actor(row.assigned),
            stamp(row.released),
            actor(row.released),
        ]),
    );
}
